import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where, doc, addDoc, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useLang, LangToggle } from '../LanguageContext';
import { t } from '../i18n';

const COULEUR = '#1C2833';

export default function ObjectifsKPIs({ onClose }) {
  const [etape, setEtape] = useState(1); // 1=bibliothèque, 2=assignation
  const [objectifs, setObjectifs] = useState([]);
  const [commerciaux, setCommerciaux] = useState([]);
  const [assignations, setAssignations] = useState([]);
  const [commSelectionne, setCommSelectionne] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const { lang, isRTL } = useLang();

  const [form, setForm] = useState({
    designation: '',
    objectif: '',
    periode: '/ mois',
    unite: 'Nombre',
    uniteAutre: '',
    periodeAutre: '',
    tauxMin: '66',
    depassement: false,
  });

  const PERIODES = ['/ mois', '/ semaine', '/ trimestre', '/ jour', '/ semestre'];
  const UNITES = ['Nombre', '% (pourcentage)', 'DA (montant)', 'Jours', 'Poids (kg)', 'Unité'];
  const COLORS = ['#C0392B', '#185FA5', '#3B6D11', '#854F0B', '#1D9E75'];

  useEffect(() => { charger(); }, []);

  const charger = async () => {
    setLoading(true);
    try {
      const oSnap = await getDocs(collection(db, 'objectifs'));
      setObjectifs(oSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      const uSnap = await getDocs(query(collection(db, 'users'), where('role', '==', 'commercial')));
      setCommerciaux(uSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      const aSnap = await getDocs(collection(db, 'assignations'));
      setAssignations(aSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) { console.log(e); }
    setLoading(false);
  };

  const enregistrerObjectif = async () => {
    if (!form.designation.trim() || !form.objectif) return;
    setSaving(true);
    try {
      const data = {
        designation: form.designation,
        objectif: parseFloat(form.objectif),
        periode: form.periode === 'Autre' ? form.periodeAutre : form.periode,
        unite: form.unite === 'Autre' ? form.uniteAutre : form.unite,
        tauxMin: parseInt(form.tauxMin),
        depassement: form.depassement,
        createdAt: serverTimestamp(),
      };
      await addDoc(collection(db, 'objectifs'), data);
      setForm({ designation: '', objectif: '', periode: '/ mois', unite: 'Nombre', uniteAutre: '', periodeAutre: '', tauxMin: '66', depassement: false });
      setShowForm(false);
      await charger();
    } catch (e) { alert('Erreur: ' + e.message); }
    setSaving(false);
  };

  const supprimerObjectif = async (id) => {
    if (!window.confirm('Supprimer cet objectif ?')) return;
    try {
      await deleteDoc(doc(db, 'objectifs', id));
      await charger();
    } catch (e) { alert('Erreur: ' + e.message); }
  };

  // ASSIGNATION
  const getAssignation = (commId) => assignations.find(a => a.commercialId === commId) || { objectifsRatios: {}, variableTotal: 0 };

  const totalRatio = (commId) => {
    const a = getAssignation(commId);
    return Object.values(a.objectifsRatios || {}).reduce((s, v) => s + (v || 0), 0);
  };

  const updateRatio = async (commId, objId, ratio) => {
    const existing = assignations.find(a => a.commercialId === commId);
    const newRatios = { ...(existing?.objectifsRatios || {}), [objId]: parseInt(ratio) || 0 };
    if (existing) {
      await updateDoc(doc(db, 'assignations', existing.id), { objectifsRatios: newRatios });
    } else {
      await addDoc(collection(db, 'assignations'), { commercialId: commId, objectifsRatios: newRatios, variableTotal: commSelectionne?.variableTotal || 0 });
    }
    await charger();
  };

  const updateVariable = async (commId, val) => {
    const existing = assignations.find(a => a.commercialId === commId);
    if (existing) {
      await updateDoc(doc(db, 'assignations', existing.id), { variableTotal: parseInt(val) || 0 });
    } else {
      await addDoc(collection(db, 'assignations'), { commercialId: commId, objectifsRatios: {}, variableTotal: parseInt(val) || 0 });
    }
    await charger();
  };

  const assignation = commSelectionne ? getAssignation(commSelectionne.id) : null;
  const varTotal = assignation?.variableTotal || commSelectionne?.variableTotal || 0;
  const total = commSelectionne ? totalRatio(commSelectionne.id) : 0;

  return (
    <div style={{ ...styles.app, direction: isRTL ? 'rtl' : 'ltr' }}>
      <div style={styles.topbar}>
        <button onClick={onClose} style={styles.backBtn}>←</button>
        <div style={{ flex: 1 }}>
          <div style={styles.topbarTitle}>Objectifs & KPIs</div>
          <div style={styles.topbarSub}>{etape === 1 ? 'Bibliothèque d\'objectifs' : 'Assignation au commercial'}</div>
        </div>
      </div>

      {/* ONGLETS */}
      <div style={styles.tabs}>
        <div style={{ ...styles.tab, ...(etape === 1 ? styles.tabActive : {}) }} onClick={() => setEtape(1)}>
          <div style={{ ...styles.tabNum, ...(etape === 1 ? styles.tabNumActive : {}) }}>1</div>
          Objectifs
        </div>
        <div style={{ ...styles.tab, ...(etape === 2 ? styles.tabActive : {}) }} onClick={() => setEtape(2)}>
          <div style={{ ...styles.tabNum, ...(etape === 2 ? styles.tabNumActive : {}) }}>2</div>
          Assignation
        </div>
      </div>

      {/* PAGE 1 : BIBLIOTHÈQUE */}
      {etape === 1 && (
        <div style={styles.content}>

          {/* FORMULAIRE CREATION */}
          {showForm && (
            <div style={styles.formCard}>
              <div style={styles.formTitle}>Créer un objectif</div>

              <div style={styles.field}>
                <div style={styles.label}>t(lang, 'designation') *</div>
                <input style={styles.input} placeholder="Ex: Nouveaux clients, Chiffre d'affaires..."
                  value={form.designation} onChange={e => setForm(f => ({ ...f, designation: e.target.value }))} autoFocus />
              </div>

              <div style={styles.field}>
                <div style={styles.label}>Objectif *</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input type="number" style={{ ...styles.input, flex: 1 }} placeholder="Ex: 3"
                    value={form.objectif} onChange={e => setForm(f => ({ ...f, objectif: e.target.value }))} />
                  <select style={{ ...styles.select, flex: 1 }} value={form.periode}
                    onChange={e => setForm(f => ({ ...f, periode: e.target.value }))}>
                    {PERIODES.map(p => <option key={p}>{p}</option>)}
                    <option value="Autre">+ Autre...</option>
                  </select>
                </div>
                {form.periode === 'Autre' && (
                  <input style={{ ...styles.input, marginTop: 6 }} placeholder="Précisez la période..."
                    value={form.periodeAutre} onChange={e => setForm(f => ({ ...f, periodeAutre: e.target.value }))} />
                )}
              </div>

              <div style={styles.field}>
                <div style={styles.label}>t(lang, 'unite') de mesure *</div>
                <select style={styles.select} value={form.unite}
                  onChange={e => setForm(f => ({ ...f, unite: e.target.value }))}>
                  {UNITES.map(u => <option key={u}>{u}</option>)}
                  <option value="Autre">+ Autre...</option>
                </select>
                {form.unite === 'Autre' && (
                  <input style={{ ...styles.input, marginTop: 6 }} placeholder="Ex: Tonnes, Litres..."
                    value={form.uniteAutre} onChange={e => setForm(f => ({ ...f, uniteAutre: e.target.value }))} />
                )}
              </div>

              <div style={styles.field}>
                <div style={styles.label}>Taux minimum pour déclencher la prime</div>
                <div style={styles.tauxPill}>
                  <input type="number" style={styles.tauxInput} value={form.tauxMin} min="0" max="100"
                    onChange={e => setForm(f => ({ ...f, tauxMin: e.target.value }))} />
                  <span style={styles.tauxPct}>%</span>
                  <div style={styles.tauxSep}></div>
                  <div style={styles.tauxHint}>En dessous → <span style={{ color: '#A32D2D', fontWeight: 500 }}>aucune prime</span></div>
                </div>
              </div>

              <div style={styles.depCard}>
                <div style={styles.depRow}>
                  <div style={{ ...styles.depIcon, ...(form.depassement ? styles.depIconOn : {}) }}>
                    <span style={{ fontSize: 17 }}>📈</span>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={styles.depTitle}>Récompenser le dépassement</div>
                    <div style={styles.depSub}>Bonus si l'objectif est dépassé</div>
                  </div>
                  <div style={{ ...styles.toggle, ...(form.depassement ? styles.toggleOn : {}) }}
                    onClick={() => setForm(f => ({ ...f, depassement: !f.depassement }))}>
                    <div style={{ ...styles.toggleKnob, left: form.depassement ? 27 : 3 }}></div>
                  </div>
                </div>
                {form.depassement && (
                  <div style={styles.depDetail}>
                    Pour chaque <strong>10% de dépassement</strong>, le commercial reçoit <strong>+10%</strong> de sa prime assignée.
                    <div style={styles.depNote}>
                      Le bonus est versé <strong>en plus</strong> du variable assigné — il ne le remplace pas.
                    </div>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                <button style={styles.btnCancel} onClick={() => setShowForm(false)}>Annuler</button>
                <button style={{ ...styles.btnSave, opacity: form.designation && form.objectif ? 1 : 0.4 }}
                  onClick={enregistrerObjectif} disabled={saving || !form.designation || !form.objectif}>
                  {saving ? 'Enregistrement...' : '✅ Enregistrer'}
                </button>
              </div>
            </div>
          )}

          {/* LISTE OBJECTIFS */}
          <div style={styles.sectionLabel}>Bibliothèque ({objectifs.length})</div>

          {loading && <div style={styles.empty}>Chargement...</div>}
          {!loading && objectifs.length === 0 && (
            <div style={styles.empty}>
              <div style={{ fontSize: 32 }}>🎯</div>
              <div style={{ marginTop: 8, color: '#888' }}>Aucun objectif créé</div>
              <div style={{ fontSize: 12, color: '#aaa', marginTop: 4 }}>Créez vos premiers objectifs ci-dessous</div>
            </div>
          )}

          {objectifs.map((o, i) => (
            <div key={o.id} style={styles.objCard}>
              <div style={styles.objHeader}>
                <div style={{ ...styles.objIcon, background: COLORS[i % COLORS.length] + '20', color: COLORS[i % COLORS.length] }}>
                  🎯
                </div>
                <div style={{ flex: 1 }}>
                  <div style={styles.objNom}>{o.designation}</div>
                  <div style={styles.objSub}>
                    {o.objectif} {o.unite} {o.periode} · Min. {o.tauxMin}%
                    {o.depassement && ' · 📈 Dépassement'}
                  </div>
                </div>
                <button style={styles.delBtn} onClick={() => supprimerObjectif(o.id)}>✕</button>
              </div>
            </div>
          ))}

          <button style={styles.addBtn} onClick={() => setShowForm(true)}>
            + Créer un objectif
          </button>

          {objectifs.length > 0 && (
            <button style={styles.nextBtn} onClick={() => setEtape(2)}>
              Étape 2 : Assignation → 
            </button>
          )}
        </div>
      )}

      {/* PAGE 2 : ASSIGNATION */}
      {etape === 2 && (
        <div style={styles.content}>

          {/* SÉLECTION COMMERCIAL */}
          <div style={styles.sectionLabel}>Commercial</div>
          <div style={styles.commPills}>
            {commerciaux.map((c, i) => (
              <div key={c.id}
                style={{ ...styles.commPill, ...(commSelectionne?.id === c.id ? styles.commPillActive : {}) }}
                onClick={() => setCommSelectionne(c)}>
                <div style={{ ...styles.commAv, background: ['#FCEBEB', '#E6F1FB', '#EAF3DE'][i % 3], color: ['#C0392B', '#185FA5', '#3B6D11'][i % 3] }}>
                  {(c.prenom || 'C')[0]}
                </div>
                <span style={styles.commPillName}>{c.prenom}</span>
              </div>
            ))}
          </div>

          {commSelectionne && (
            <>
              {/* VARIABLE TOTAL */}
              <div style={styles.varCard}>
                <div style={{ flex: 1 }}>
                  <div style={styles.varLabel}>Variable total — {commSelectionne.prenom}</div>
                  <div style={styles.varRow}>
                    <input type="number" style={styles.varInput}
                      defaultValue={varTotal}
                      onBlur={e => updateVariable(commSelectionne.id, e.target.value)} />
                    <span style={styles.varUnit}>DA / mois</span>
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: total === 100 ? '#3B6D11' : total > 100 ? '#C0392B' : '#854F0B' }}>
                    {total}%
                  </div>
                  <div style={{ fontSize: 10, color: '#888' }}>distribué</div>
                </div>
              </div>

              {/* BARRE RATIO */}
              <div style={styles.ratioBar}>
                {objectifs.map((o, i) => {
                  const ratio = assignation?.objectifsRatios?.[o.id] || 0;
                  return ratio > 0 ? (
                    <div key={o.id} style={{ width: `${ratio}%`, height: 8, background: COLORS[i % COLORS.length], transition: 'width 0.3s' }}></div>
                  ) : null;
                })}
                <div style={{ flex: 1, height: 8, background: '#f0f0f0' }}></div>
              </div>

              {/* INDICATEUR TOTAL */}
              <div style={{ ...styles.totalCheck, background: total === 100 ? '#EAF3DE' : total > 100 ? '#FCEBEB' : '#FAEEDA' }}>
                <span style={{ fontSize: 13, fontWeight: 500, color: total === 100 ? '#3B6D11' : total > 100 ? '#A32D2D' : '#854F0B' }}>
                  {total === 100 ? '✓ Total : 100% — Parfait !' : total > 100 ? `✕ Total : ${total}% — Dépasse 100%` : `⚠ Total : ${total}% — Reste ${100 - total}% à distribuer`}
                </span>
                <span style={{ fontSize: 14, fontWeight: 600, color: total === 100 ? '#3B6D11' : '#854F0B' }}>
                  {Math.round(varTotal * total / 100).toLocaleString()} DA
                </span>
              </div>

              {/* OBJECTIFS À ASSIGNER */}
              <div style={styles.sectionLabel}>t(lang, 'assigner_objectifs')</div>
              {objectifs.map((o, i) => {
                const ratio = assignation?.objectifsRatios?.[o.id] || 0;
                const da = Math.round(varTotal * ratio / 100);
                return (
                  <div key={o.id} style={styles.assignRow}>
                    <div style={{ ...styles.assignIcon, background: COLORS[i % COLORS.length] + '20', color: COLORS[i % COLORS.length] }}>🎯</div>
                    <div style={{ flex: 1 }}>
                      <div style={styles.objNom}>{o.designation}</div>
                      <div style={styles.objSub}>{o.objectif} {o.unite} {o.periode}</div>
                      <div style={{ fontSize: 11, color: COLORS[i % COLORS.length], fontWeight: 600, marginTop: 2 }}>
                        {da > 0 ? `${da.toLocaleString()} DA` : '0 DA'}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <input type="number" style={styles.ratioInput} defaultValue={ratio} min="0" max="100"
                        key={o.id + '-' + commSelectionne.id}
                        onBlur={e => updateRatio(commSelectionne.id, o.id, e.target.value)} />
                      <span style={{ fontSize: 12, color: '#888' }}>%</span>
                    </div>
                  </div>
                );
              })}

              {objectifs.length === 0 && (
                <div style={styles.empty}>
                  <div>Créez d'abord des objectifs à l'étape 1</div>
                  <button style={{ ...styles.addBtn, marginTop: 10 }} onClick={() => setEtape(1)}>← Étape 1</button>
                </div>
              )}
            </>
          )}

          {!commSelectionne && (
            <div style={styles.empty}>
              <div style={{ fontSize: 32 }}>👆</div>
              <div style={{ marginTop: 8, color: '#888' }}>Sélectionnez un commercial</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const styles = {
  app: { maxWidth: 480, margin: '0 auto', minHeight: '100vh', background: '#f5f5f5', display: 'flex', flexDirection: 'column', fontFamily: 'system-ui, sans-serif' },
  topbar: { background: COULEUR, padding: '1rem 1.25rem', color: '#fff', display: 'flex', alignItems: 'center', gap: 10 },
  backBtn: { background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', width: 32, height: 32, color: '#fff', fontSize: 16, cursor: 'pointer', flexShrink: 0 },
  topbarTitle: { fontSize: 15, fontWeight: 600 },
  topbarSub: { fontSize: 11, opacity: 0.7, marginTop: 2 },
  tabs: { display: 'flex', background: '#fff', borderBottom: '1px solid #eee' },
  tab: { flex: 1, padding: '10px 4px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: 13, color: '#888', cursor: 'pointer', borderBottom: '2px solid transparent' },
  tabActive: { color: COULEUR, borderBottomColor: COULEUR, fontWeight: 500 },
  tabNum: { width: 20, height: 20, borderRadius: '50%', background: '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 500 },
  tabNumActive: { background: COULEUR, color: '#fff' },
  content: { flex: 1, overflowY: 'auto', padding: '12px', display: 'flex', flexDirection: 'column', gap: 10 },
  sectionLabel: { fontSize: 11, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 },
  formCard: { background: '#fff', borderRadius: 12, padding: 16, border: `1px solid ${COULEUR}`, display: 'flex', flexDirection: 'column', gap: 12 },
  formTitle: { fontSize: 15, fontWeight: 600, color: COULEUR },
  field: { display: 'flex', flexDirection: 'column', gap: 5 },
  label: { fontSize: 12, color: '#666', fontWeight: 500 },
  input: { padding: '11px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, fontFamily: 'system-ui', width: '100%' },
  select: { padding: '11px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, fontFamily: 'system-ui', width: '100%' },
  tauxPill: { display: 'flex', alignItems: 'center', gap: 10, background: '#fff', border: '1px solid #ddd', borderRadius: 8, padding: '8px 14px' },
  tauxInput: { width: 60, padding: '6px', border: '1px solid #ddd', borderRadius: 6, fontSize: 16, fontWeight: 500, textAlign: 'center', fontFamily: 'system-ui' },
  tauxPct: { fontSize: 16, fontWeight: 500, color: '#666' },
  tauxSep: { width: 1, height: 28, background: '#eee' },
  tauxHint: { fontSize: 12, color: '#888', flex: 1 },
  depCard: { background: '#fff', borderRadius: 10, border: '1px solid #eee', overflow: 'hidden' },
  depRow: { padding: '12px', display: 'flex', alignItems: 'center', gap: 10 },
  depIcon: { width: 36, height: 36, borderRadius: 8, background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  depIconOn: { background: '#FAEEDA' },
  depTitle: { fontSize: 13, fontWeight: 500, color: '#1C2833' },
  depSub: { fontSize: 11, color: '#888', marginTop: 2 },
  toggle: { width: 52, height: 28, borderRadius: 99, background: '#e0e0e0', position: 'relative', cursor: 'pointer', transition: 'background 0.2s', flexShrink: 0 },
  toggleOn: { background: '#C0392B' },
  toggleKnob: { width: 22, height: 22, borderRadius: '50%', background: '#fff', position: 'absolute', top: 3, transition: 'left 0.2s', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' },
  depDetail: { padding: '12px', background: '#FAEEDA', borderTop: '1px solid #eee', fontSize: 12, color: '#6B3A08', lineHeight: 1.6 },
  depNote: { marginTop: 8, background: '#854F0B', color: '#fff', borderRadius: 8, padding: '6px 10px', fontSize: 11 },
  btnCancel: { flex: 1, padding: 11, border: '1px solid #ddd', borderRadius: 10, background: '#fff', color: '#666', fontSize: 14, cursor: 'pointer' },
  btnSave: { flex: 2, padding: 11, background: COULEUR, border: 'none', borderRadius: 10, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  empty: { textAlign: 'center', padding: '2rem', color: '#888', display: 'flex', flexDirection: 'column', alignItems: 'center' },
  objCard: { background: '#fff', borderRadius: 10, padding: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  objHeader: { display: 'flex', alignItems: 'center', gap: 10 },
  objIcon: { width: 34, height: 34, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  objNom: { fontSize: 13, fontWeight: 600, color: '#1C2833' },
  objSub: { fontSize: 11, color: '#888', marginTop: 2 },
  delBtn: { background: '#FCEBEB', border: 'none', borderRadius: 6, color: '#A32D2D', fontSize: 12, cursor: 'pointer', padding: '4px 8px', flexShrink: 0 },
  addBtn: { width: '100%', padding: 12, border: '1px dashed #ccc', borderRadius: 10, background: 'none', color: '#666', fontSize: 13, cursor: 'pointer' },
  nextBtn: { width: '100%', padding: 12, background: COULEUR, border: 'none', borderRadius: 10, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  commPills: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  commPill: { display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 99, border: '1px solid #ddd', background: '#fff', cursor: 'pointer' },
  commPillActive: { border: `1px solid ${COULEUR}`, background: '#f0f4f8' },
  commAv: { width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 600 },
  commPillName: { fontSize: 13, color: '#1C2833' },
  varCard: { background: '#fff', borderRadius: 10, padding: '12px 14px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', gap: 12 },
  varLabel: { fontSize: 12, color: '#888' },
  varRow: { display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 },
  varInput: { width: 100, padding: '7px 10px', border: '1px solid #ddd', borderRadius: 8, fontSize: 15, fontWeight: 500, fontFamily: 'system-ui' },
  varUnit: { fontSize: 12, color: '#888' },
  ratioBar: { display: 'flex', borderRadius: 99, overflow: 'hidden', height: 8 },
  totalCheck: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', borderRadius: 10 },
  assignRow: { background: '#fff', borderRadius: 10, padding: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', gap: 10 },
  assignIcon: { width: 34, height: 34, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  ratioInput: { width: 52, padding: '7px 8px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, fontWeight: 500, textAlign: 'center', fontFamily: 'system-ui' },
};
